import { useState, useMemo, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { Popover, Tooltip } from '@material-ui/core';
import { getRecoil } from 'recoil-nexus';
import Utils from '@/common/utils/utils';
import AttachFileThumbnailIcon from '@/component/common/icons/AttachFileThumbnailIcon';
import {
    resolveAttachIconSize,
    resolveAttachBadgeMetrics,
    getFileExtInfo,
    getExtFromFileName,
    ATTACH_BADGE
} from '@/component/common/icons/AttachUtils';
import { replacedFileTimestampMapAtom } from '@/state/atom/ticketAtom';
import './AttachFileThumbnail.css';

/**
 * One attachment thumbnail: the file-type icon (or an image preview for image files),
 * a [more] trigger, and a Popover that renders a caller-provided list of option items.
 *
 * The option items themselves (and their business logic such as "copy share link" or
 * "split PDF") are supplied by the caller via `options`, so this component stays generic
 * and can be reused by both the ticket list and the knowledge list. Each option is only
 * rendered when its `visible` flag is not explicitly false.
 *
 * @param {object}   props
 * @param {string}   props.fileName   - File name (drives icon ext/label; shown in tooltip).
 * @param {string}  [props.imageUrl]  - When the file is an image, the thumbnail src.
 * @param {('xs'|'sm'|'md'|'lg'|'xl'|'xxl'|number)} [props.size] - Icon size preset.
 * @param {function}[props.onClickFile] - Click on the icon/image (e.g. preview/download).
 * @param {Array<{ key: string, visible?: boolean, content: React.ReactNode }>} [props.options]
 *        - Option items rendered inside the Popover.
 * @param {boolean} [props.showMore] - Force-hide the more trigger (defaults to: show when
 *        there is at least one visible option).
 * @param {function}[props.onClickMore] - When provided, the [more] trigger calls this with the
 *        click event INSTEAD of opening the internal Popover. Lets a caller keep its own shared
 *        Popover/menu (e.g. the ticket-detail field lists) while still reusing this thumbnail UI.
 *        When set, `options` is ignored and the trigger always shows (unless `showMore === false`).
 * @param {string}  [props.tooltipSuffix] - Extra text appended to the hover tooltip after
 *        "fileName (fileSize)" (e.g. a K-board upload timestamp).
 */
function AttachFileThumbnail({
    fileName = '',
    fileSize,
    fileKey,
    imageUrl,
    size = 'md',
    onClickFile,
    options = [],
    showMore,
    badgeText,
    badgeColor,
    onClickMore,
    tooltipSuffix
}) {
    const [anchorEl, setAnchorEl] = useState(null);

    // Stable image src to stop the thumbnail from flickering on every list/detail reload.
    // getLinkDownloadFileFromS3 AES-encrypts the url with a random salt, so callers that compute
    // `imageUrl` inline hand us a NEW url for the SAME file on every render -> the <img> reloads.
    // We keep the first url seen for a given cache key and reuse it; a later url with the same key
    // (only the random part changed) is ignored, while a new key flows through as a new src.
    // The key is: a unique file id (`fileKey`, e.g. fileUrl; fallback fileName::fileSize) PLUS the
    // replace-timestamp, so replacing a file in place (same fileUrl) still busts the stable src and
    // shows the new image, matching getLinkDownloadFileFromS3's own cache-busting.
    const fileIdentity = fileKey || `${fileName}::${fileSize}`;
    const replacedTimestamp = getRecoil(replacedFileTimestampMapAtom)?.[fileKey] || '';
    const stableImageUrlRef = useRef({ key: null, url: undefined });
    const stableImageUrl = useMemo(() => {
        if (!imageUrl) return imageUrl;
        const key = `${fileIdentity}::${replacedTimestamp}`;
        if (stableImageUrlRef.current.key !== key) {
            stableImageUrlRef.current = { key, url: imageUrl };
        }
        return stableImageUrlRef.current.url;
    }, [imageUrl, fileIdentity, replacedTimestamp]);

    const isImage = Utils.isImageFile(fileName) && stableImageUrl;
    const { width: mediaWidth, height: mediaHeight } = resolveAttachIconSize(size);
    // File-type badge for the image preview. Color is grouped by type (image -> pink); the
    // label shows the RAW extension (png/jpg/...) to match the file icon. The caller may
    // override text/color (e.g. a page-knowledge file shows "page" brown).
    const rawExt = getExtFromFileName(fileName);
    const fileTypeInfo = getFileExtInfo(rawExt);
    const badgeDisplay = badgeText || rawExt || fileTypeInfo.DISPLAY;
    const badgeDisplayColor = badgeColor || fileTypeInfo.COLOR_DISPLAY;
    // Badge metrics shared with the file-icon badge; the image preview draws its own SVG.
    const { width: badgeWidth, height: badgeHeight, fontSize: badgeFontUnits } =
        resolveAttachBadgeMetrics(size, badgeDisplay);
    // Scale the more-trigger (strip + its icon) with the media height so it stays
    // proportional across all `size` presets.
    const moreIconSize = Math.round(mediaHeight * 0.3);
    const moreWidth = Math.round(mediaHeight * 0.27);
    const visibleOptions = options.filter(opt => opt && opt.visible !== false);
    // When a caller owns its own menu (onClickMore), always show the trigger (unless force-hidden);
    // otherwise show it only when there is at least one visible internal option.
    const useExternalMenu = typeof onClickMore === 'function';
    const canOpenMore = showMore !== false && (useExternalMenu || visibleOptions.length > 0);

    const openOptions = e => {
        if (useExternalMenu) {
            onClickMore(e);
            return;
        }
        setAnchorEl(e.currentTarget);
    };
    const closeOptions = () => setAnchorEl(null);

    // Tooltip on hover: "fileName.ext (fileSize)"; omit the size part when unknown.
    // `tooltipSuffix` (optional) is appended after a space, e.g. a K-board upload timestamp.
    const tooltipBase =
        fileSize === undefined || fileSize === null
            ? fileName
            : `${fileName} (${Utils.formatBytes(fileSize)})`;
    const tooltipTitle = tooltipSuffix ? `${tooltipBase} ${tooltipSuffix}` : tooltipBase;

    return (
        <div className="attach-file-thumbnail">
            <Tooltip title={tooltipTitle} aria-label={tooltipTitle} arrow>
                <div
                    className="attach-file-thumbnail-media"
                    style={{ width: mediaWidth, height: mediaHeight }}
                    onClick={onClickFile}
                >
                    {isImage ? (
                        <>
                            {/* Render the badge as SVG */}
                            <svg
                                className="attach-file-thumbnail-badge"
                                width={badgeWidth}
                                height={badgeHeight}
                                viewBox={`0 0 ${ATTACH_BADGE.VIEWBOX_WIDTH} ${ATTACH_BADGE.VIEWBOX_HEIGHT}`}
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d={ATTACH_BADGE.PATH} fill={badgeDisplayColor} />
                                <text {...ATTACH_BADGE.TEXT_PROPS} fontSize={badgeFontUnits}>
                                    {badgeDisplay}
                                </text>
                            </svg>
                            <img
                                className="attach-file-thumbnail-image"
                                src={stableImageUrl}
                                alt=""
                                width={mediaWidth}
                                height={mediaHeight}
                                loading="lazy"
                                decoding="async"
                            />
                        </>
                    ) : (
                        <AttachFileThumbnailIcon
                            classWrap="attach-file-thumbnail-file"
                            fileName={fileName}
                            size={size}
                            badgeText={badgeText}
                            badgeColor={badgeColor}
                        />
                    )}
                </div>
            </Tooltip>

            {canOpenMore && (
                <div
                    className="attach-file-thumbnail-more"
                    style={{ width: moreWidth }}
                    onClick={openOptions}
                >
                    <img
                        src="/assets/image/icon-more-panel.svg"
                        alt="attach-thumb-more-opts"
                        style={{ width: moreIconSize, height: moreIconSize }}
                    />
                </div>
            )}

            {!useExternalMenu && (
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={closeOptions}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <div className="attach-file-thumbnail-options">
                        {visibleOptions.map(opt => (
                            <div
                                key={opt.key}
                                onClick={opt.closeOnClick === false ? undefined : closeOptions}
                            >
                                {opt.content}
                            </div>
                        ))}
                    </div>
                </Popover>
            )}
        </div>
    );
}

AttachFileThumbnail.propTypes = {
    fileName: PropTypes.string,
    fileSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Unique file id used as the stable-src cache key (e.g. fileUrl). Falls back to fileName+fileSize.
    fileKey: PropTypes.string,
    imageUrl: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onClickFile: PropTypes.func,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            visible: PropTypes.bool,
            content: PropTypes.node,
            // Close the Popover after click (default true); set false to keep it open.
            closeOnClick: PropTypes.bool
        })
    ),
    showMore: PropTypes.bool,
    badgeText: PropTypes.string,
    badgeColor: PropTypes.string,
    // When provided, the [more] trigger calls this instead of opening the internal Popover.
    onClickMore: PropTypes.func,
    // Optional text appended to the hover tooltip after "fileName (fileSize)".
    tooltipSuffix: PropTypes.string
};

export default AttachFileThumbnail;
